import { SITE } from "@/content/site";
import { RANGPUR_AREAS, type RangpurArea, type RangpurAreaMeta } from "@/content/rangpur-areas";
import type { FaqItem } from "@/components/FaqAccordion";

export type RelatedService = {
  label: string;
  to: string;
  hash?: string;
  params?: Record<string, string>;
  why: string;
};

/**
 * Everything derived from a single Rangpur neighbourhood record:
 * LocalBusiness schema, map links, nearest neighbours, topically relevant
 * service links and hyperlocal FAQs. Kept out of the route file so the page
 * component stays presentational.
 */
export function buildRangpurArea(a: RangpurArea) {
  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE.url}/seo-services/rangpur/${a.slug}#business`,
    name: `Freelancer Tamal — SEO Expert near ${a.name}`,
    description: `Local SEO, Google Business Profile, and technical SEO for businesses near ${a.name} in Rangpur, Bangladesh.`,
    url: `${SITE.url}/seo-services/rangpur/${a.slug}`,
    image: `${SITE.url}/og-image.jpg`,
    telephone: SITE.phone,
    email: SITE.email,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: a.name,
      addressLocality: "Rangpur",
      addressRegion: "Rangpur Division",
      addressCountry: "BD",
    },
    geo: { "@type": "GeoCoordinates", latitude: a.geo.lat, longitude: a.geo.lng },
    areaServed: {
      "@type": "Place",
      name: `${a.name}, Rangpur`,
      geo: {
        "@type": "GeoCircle",
        geoMidpoint: { "@type": "GeoCoordinates", latitude: a.geo.lat, longitude: a.geo.lng },
        geoRadius: 1500,
      },
    },
    serviceType: ["Local SEO", "Google Business Profile Optimization", "Technical SEO", "Content Strategy"],
    provider: { "@id": `${SITE.url}/#person` },
    sameAs: Object.values(SITE.social),
  };

  const mapEmbedSrc = `https://www.google.com/maps?q=${a.geo.lat},${a.geo.lng}&z=15&output=embed`;
  const mapsLink = `https://www.google.com/maps/search/?api=1&query=${a.geo.lat},${a.geo.lng}`;

  const others = RANGPUR_AREAS.filter((x) => x.slug !== a.slug);

  // Nearest neighborhoods by haversine distance — surface top 4 as
  // contextual "next click" links for users and crawlers.
  const toRad = (n: number) => (n * Math.PI) / 180;
  const distanceKm = (b: RangpurAreaMeta) => {
    const R = 6371;
    const dLat = toRad(b.geo.lat - a.geo.lat);
    const dLng = toRad(b.geo.lng - a.geo.lng);
    const s =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(a.geo.lat)) * Math.cos(toRad(b.geo.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(s));
  };
  const neighbors = [...others]
    .map((o) => ({ area: o, km: distanceKm(o) }))
    .sort((x, y) => x.km - y.km)
    .slice(0, 4);

  // Map area business categories to the most relevant service pages so each
  // area cross-links to the right topical hub (boosts topical authority).
  const text = (a.businesses.join(" ") + " " + a.landmarks.join(" ")).toLowerCase();
  const has = (...kw: string[]) => kw.some((k) => text.includes(k));
  const relatedServices: { label: string; to: string; hash?: string; params?: Record<string, string>; why: string }[] = [
    {
      label: "Local SEO & Google Business Profile",
      to: "/services",
      hash: "local-seo",
      why: `Win the "near ${a.name}" map pack with GBP, citations and review velocity.`,
    },
  ];
  if (has("clinic", "diagnostic", "pharmac", "medical", "hospital")) {
    relatedServices.push({
      label: "Healthcare & clinic SEO",
      to: "/services",
      hash: "local-seo",
      why: `Patient-intent queries dominate around ${a.name} — review velocity and service-page schema decide rankings.`,
    });
  }
  if (has("retail", "showroom", "fashion", "garment", "grocery", "wholesale", "bazar", "bazaar", "electronics", "mobile")) {
    relatedServices.push({
      label: "Ecommerce & retail SEO",
      to: "/services",
      hash: "seo",
      why: `Product, category and store-locator SEO for ${a.name}-area retailers and showrooms.`,
    });
  }
  if (has("coaching", "tutoring", "university", "college", "student", "education", "hostel", "mess")) {
    relatedServices.push({
      label: "SEO training & workshops",
      to: "/seo-training",
      why: `In-person SEO coaching is available across the ${a.name} education corridor.`,
    });
  }
  if (has("legal", "notary", "financial", "accounting", "bank", "insurance", "b2b", "professional")) {
    relatedServices.push({
      label: "Technical SEO audit",
      to: "/services",
      hash: "technical-seo-audit",
      why: `High-trust ${a.name} buyers convert on fast, indexable, schema-rich sites.`,
    });
  }
  if (has("restaurant", "café", "cafe", "food", "hospitality", "hotel", "guest", "event", "tour")) {
    relatedServices.push({
      label: "Content & local SEO strategy",
      to: "/services",
      hash: "digital-marketing",
      why: `Bangla-first content and review strategy for ${a.name} hospitality and food businesses.`,
    });
  }
  // Always offer the AI-search angle and the free audit.
  relatedServices.push({
    label: "AEO & GEO (AI search)",
    to: "/services/aeo-geo",
    why: `Earn citations in ChatGPT, Perplexity and Google AI Overviews for ${a.name}-area queries.`,
  });
  relatedServices.push({
    label: "Free SEO audit",
    to: "/free-seo-audit",
    why: `Instant audit of any ${a.name} business site — title, schema, indexability and Core Web Vitals.`,
  });

  // Hyperlocal FAQs — woven with real intent terms (Bangla aliases,
  // category words, landmarks) so Google can disambiguate the page fast.
  const aliasLine = a.aliases.length ? a.aliases.join(", ") : a.name;
  const topCats = a.businesses.slice(0, 3).join(", ");
  const landmark = a.landmarks[0] ?? a.name;
  // Prefer hand-written area-specific FAQs when available, so two
  // enriched area pages never ship the same FAQ block.
  const faqs: FaqItem[] = a.localFaqs?.length
    ? a.localFaqs
    : [
    {
      q: `Do you offer SEO services near ${a.name} in Rangpur?`,
      a: `Yes — I provide hyperlocal SEO, Google Business Profile optimization, technical SEO, and content strategy for businesses operating near ${a.name} (also searched as ${aliasLine}). On-site visits across Rangpur are available, and most engagements run remotely with weekly reporting.`,
    },
    {
      q: `How much does local SEO near ${a.name} cost?`,
      a: `It depends on your site size, category and how strong the competition is in the ${a.name} map pack. I usually start with an audit, then quote any monthly work in writing based on what the audit finds — not a generic package. I don't publish a fixed area price because scopes vary widely.`,
    },
    {
      q: `Which businesses near ${a.name} benefit most from SEO?`,
      a: `${topCats}${a.businesses.length > 3 ? ", and similar local-intent businesses" : ""} see the fastest returns. If buyers around ${landmark} search Google or Maps before walking in or calling, hyperlocal SEO is the highest-ROI marketing move you can make.`,
    },
    {
      q: `How long does it take to rank in the ${a.name} map pack?`,
      a: `There's no guaranteed timeline. Map-pack movement depends on your category, nearby competitors, distance from searchers and genuine review activity. The first step is fixing categories, services, photos, posts and citations; after that I report your position monthly so you can see what is and isn't changing.`,
    },
    {
      q: `Do you work with Bangla-language searches around ${a.name}?`,
      a: `Yes. A large share of ${a.name} buyer queries are Bangla or Banglish (e.g. "${a.aliases[0] ?? a.name} এর কাছে"). I build bilingual on-page content, GBP fields, and review-response templates so you capture both English and Bangla long-tail intent.`,
    },
    {
      q: `Do I need a separate website for the ${a.name} location?`,
      a: `Not usually. A single well-structured site with a dedicated /near/${a.slug}-style landing page (with embedded map, hours, reviews, FAQ schema, and local content) is enough to rank for "${a.name}"-style queries. I'll set that page up as part of the engagement.`,
    },
  ];

  return { businessSchema, mapEmbedSrc, mapsLink, others, neighbors, relatedServices, faqs };
}
